import Text from "@/components/Text";
import { Button } from "@/components/ui/button";
import { type Dispatch, type FC, type SetStateAction } from "react";
import type { View } from "../..";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router";
import { Check, PuzzleIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

type Props = {
  setView?: Dispatch<SetStateAction<View>>;
  haveUnsubscribeAction?: boolean;
  onOpenUnsubDialog?: () => void;
  isLoading: boolean;
  productName?: string;
  haveUpgradeAction?: boolean;
  haventManageAction?: boolean;
  expirationDate?: string;
};

const CurrentSubCard: FC<Props> = ({
  setView,
  haveUnsubscribeAction,
  onOpenUnsubDialog,
  isLoading,
  productName,
  haveUpgradeAction,
  haventManageAction,
  expirationDate,
}) => {
  const { t } = useTranslation();

  if (isLoading) return <Skeleton className="w-full h-[600] rounded-[12px]" />;

  return (
    <div>
      <div className="rounded-[12px] p-[24px] flex flex-col">
        <div className="flex justify-between flex-col sm:flex-row gap-[12px]">
          <div className="flex flex-col gap-[4px]">
            <Text fontWeight={600} fontSize={16}>
              {productName ?? t("free")}
            </Text>
            {expirationDate && (
              <Text fontSize={14} fontWeight={400}>
                {t("expirated_at", {
                  date: dayjs(expirationDate).format("DD/MM/YYYY"),
                })}
              </Text>
            )}
          </div>
          <div className="flex items-center gap-[16px]">
            <Link to="/upgrade">
              <Button variant="secondaryFilled">{t("see_plans")}</Button>
            </Link>
            {haveUpgradeAction && (
              <Link className="hidden sm:inline-flex" to="/upgrade">
                <Button variant="primaryFilled">
                  <PuzzleIcon width={16} height={16} color="white" />
                  {t("upgrade")}
                </Button>
              </Link>
            )}
            {haveUnsubscribeAction && (
              <Button
                variant="dangerFilled"
                onClick={() => onOpenUnsubDialog?.()}
              >
                {t("unsubscribe")}
              </Button>
            )}
            {!haveUnsubscribeAction &&
              !haveUpgradeAction &&
              !haventManageAction && (
                <Button
                  variant="secondaryFilled"
                  onClick={() => setView?.("subscriptions")}
                >
                  {t("manage_plan")}
                </Button>
              )}
          </div>
        </div>
      </div>
      {!productName && (
        <div className="flex flex-col justify-between bg-[white] py-[24px] px-[16px] rounded-[12px]">
          <div className="flex flex-col gap-[32px]">
            <div className="py-[24px] px-[16px] bg-[#000024] gap-[8px] flex flex-col rounded-[8px]">
              <Text fontWeight={500} fontSize={32} color="white">
                {t("starter")}
              </Text>
              <Text fontWeight={400} fontSize={14} color="white">
                {t("observe")}
              </Text>
            </div>
            <div className="flex flex-col gap-[16px]">
              <Text fontSize={28} fontWeight={400}>
                {t("free_price")}
              </Text>
              <div className="flex flex-col gap-[12px]">
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("free_card_1")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("free_card_2")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("free_card_3")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("free_card_4")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("free_card_5")}
                  </Text>
                </div>
              </div>
            </div>
          </div>

          <Button className="mt-[36px] h-[40px]" variant="secondaryFilled">
            {t("current_plan")}
          </Button>
        </div>
      )}
      {["Radar", "Network", "Pro"].includes(productName ?? "") && (
        <div className="flex flex-col justify-between bg-[white] py-[24px] px-[16px] rounded-[12px]">
          <div className="flex flex-col gap-[32px]">
            <div className="py-[24px] px-[16px] bg-[#000024] gap-[8px] flex flex-col rounded-[8px]">
              <Text fontWeight={500} fontSize={32} color="white">
                {t("pro")}
              </Text>
              <Text fontWeight={400} fontSize={14} color="white">
                {t("understand_market")}
              </Text>
            </div>
            <div className="flex flex-col gap-[16px]">
              <Text fontSize={28} fontWeight={400}>
                {t("pro_price")}
              </Text>
              <div className="flex flex-col gap-[12px]">
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_1")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_2")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_3")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_4")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_5")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_6")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_7")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_8")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_9")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_10")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_11")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("core_card_12")}
                  </Text>
                </div>
              </div>
            </div>
          </div>

          <Button className="mt-[36px] h-[40px]" variant="secondaryFilled">
            {t("current_plan")}
          </Button>
        </div>
      )}

      {productName === "Network" && (
        <div className="flex flex-col justify-between bg-[white] py-[24px] px-[16px] rounded-[12px]">
          <div className="flex flex-col gap-[32px]">
            <div className="py-[24px] px-[16px] bg-[#000024] gap-[8px] flex flex-col rounded-[8px]">
              <Text fontWeight={500} fontSize={32} color="white">
                {t("network")}
              </Text>
              <Text fontWeight={400} fontSize={14} color="white">
                {t("scale_piloting")}
              </Text>
            </div>
            <div className="flex flex-col gap-[16px]">
              <Text fontSize={28} fontWeight={400}>
                {t("network_pricing")}
              </Text>
              <div className="flex flex-col gap-[12px]">
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_1")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_2")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_3")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_4")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_5")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_6")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_7")}
                  </Text>
                </div>
                <div className="flex items-center gap-[8px]">
                  <Check color="#0F62FE" height={16} width={16} />
                  <Text fontWeight={400} fontSize={14} color="#393939">
                    {t("network_card_8")}
                  </Text>
                </div>
              </div>
            </div>
          </div>
          <Button className="mt-[36px] h-[40px]" variant="secondaryFilled">
            {t("current_plan")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CurrentSubCard;
